<template>
  <div>
    <div class="w-full h-28 mt-3">
      <div
        class="flex justify-between font-bold"
        :class="{ 'text-zinc-300': !isSliderActive, 'text-zinc-700': isSliderActive }"
      >
        <div>{{ market.currency.sign }}{{ minValue.toLocaleString() }}</div>
        <div>{{ maxValue === invisibleMaxValue ? "All" : `${market.currency.sign}${maxValue.toLocaleString()}` }}</div>
      </div>
      <div class="relative w-full mt-6 slider" :class="{ active: isSliderActive, inactive: !isSliderActive }">
        <input
          v-model.number="maxValue"
          @input="restrictMax"
          @change="setFilter"
          :class="maxInputStyles"
          type="range"
          :step="step"
          :min="min"
          :max="invisibleMaxValue"
        />
        <input
          v-model.number="minValue"
          @input="restrictMin"
          @change="setFilter"
          :class="minInputStyles"
          type="range"
          :step="step"
          :min="min"
          :max="max"
        />
        <div class="line absolute bg-gray-300 right-0 left-0 h-3 rounded mt-[8px] mx-[2px]"></div>
        <div
          :class="{ hidden: !isSliderActive }"
          class="line absolute bg-primary h-3 rounded mt-[8px] mx-[2px]"
          :style="activeLineStyles"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { i18n } from "@/lib/vue/plugins"
import { mapState, mapActions } from "vuex"
const market = i18n.t("market")

export default {
  name: "PricesMultiRangeSlider",
  props: {
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    gap: {
      type: Number,
      default: 1,
    },
    step: {
      type: Number,
      default: 1,
    },
    selected: {
      type: Array,
      default: () => [],
    },
    desktopVariant: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    restrictMin() {
      this.turnOnSlider()
      if (this.minValue + this.gap > this.maxValue) {
        this.minValue = this.maxValue - this.gap
      }
    },
    restrictMax() {
      this.turnOnSlider()
      if (this.maxValue - this.gap < this.minValue) {
        this.maxValue = this.minValue + this.gap
      }
    },
    turnOnSlider() {
      if (!this.isSliderActive) {
        this.mergeFilter({ prices: [] })
      }
    },
    prepareMaxValue() {
      // Prepare it before applying to filters
      return this.invisibleMaxValue === this.maxValue ? this.maxVisiblePrice : this.maxValue
    },
    setFilter() {
      this.setPriceRangeFilter([this.minValue, this.prepareMaxValue()])
    },
    apply() {
      this.mergeFilter({
        price_range: [this.minValue, this.prepareMaxValue()],
      })
    },
    setInitialValues(initialPrice) {
      if (initialPrice.length > 0) {
        this.minValue = initialPrice[0]
        this.maxValue = initialPrice[1] === this.maxVisiblePrice ? this.invisibleMaxValue : initialPrice[1]
      } else {
        this.minValue = this.min
        this.maxValue = this.invisibleMaxValue
      }
    },
    reset() {
      this.minValue = this.min
      this.maxValue = this.invisibleMaxValue
    },
    setPriceRangeFilter(price_range = []) {
      // Remove the price range from the filters if it has a full range
      this.$emit("select", {
        price_range: this.min === this.minValue && this.invisibleMaxValue === this.maxValue ? [] : price_range,
      })
    },
    ...mapActions("filter", ["mergeFilter"]),
  },
  data() {
    return {
      maxVisiblePrice: 99999,
      minValue: this.min,
      maxValue: 100,
      left: 0,
      right: 0,
      market,
      inputStyles: "absolute z-10 pointer-events-none cursor-pointer appearance-none bg-transparent",
      gapLinePercent: 92, // Uses to sync with controls because they decrease their width
    }
  },
  computed: {
    isSliderActive() {
      return this.prices.length === 0
    },
    maxInputStyles() {
      // Decrease the control width to fix overlapping
      return `${this.inputStyles} right-0 !w-[calc(100%-1.8rem)]`
    },
    minInputStyles() {
      // Decrease the control width to fix overlapping
      return `${this.inputStyles} left-0 !w-[calc(100%-2.2rem)]`
    },
    activeLineStyles() {
      return `left: calc(${(this.minValue / this.invisibleMaxValue) * this.gapLinePercent}%); right: calc(${this.gapLinePercent - (this.maxValue / this.invisibleMaxValue) * this.gapLinePercent}%)`
    },
    invisibleMaxValue() {
      // It uses to show the plus postfix
      return this.max + this.step
    },
    ...mapState("filter", {
      initialPrice: ({ filters }) => filters.price_range,
      prices: ({ filters }) => filters.prices,
    }),
  },
  watch: {
    initialPrice: function (newVal) {
      // It is used to handle search input changes
      this.setInitialValues(newVal)
    },
    prices: function (newVal) {
      if (newVal.length > 0) {
        this.minValue = this.min
        this.maxValue = this.invisibleMaxValue
        this.setPriceRangeFilter()
      }
    },
  },
  mounted() {
    // Setup initial values on Mobile
    if (!this.desktopVariant) {
      this.setInitialValues(this.initialPrice)
    }
  },
}
</script>

<style>
.slider {
  input[type="range"]::-webkit-slider-thumb {
    @apply appearance-none pointer-events-auto size-10 shadow-none border-0;
  }
  input[type="range"]::-moz-range-thumb {
    @apply appearance-none pointer-events-auto size-10 border-0;
    -moz-appearance: none;
  }
  @-moz-document url-prefix() {
    .line {
      margin-top: 5px;
    }
  }
}
.active {
  input[type="range"]::-webkit-slider-thumb {
    background: url("~@/assets/range-slider-thumb-active.svg") center center no-repeat;
  }
  input[type="range"]::-moz-range-thumb {
    background: url("~@/assets/range-slider-thumb-active.svg") center center no-repeat;
  }
}
.inactive {
  input[type="range"]::-webkit-slider-thumb {
    background: url("~@/assets/range-slider-thumb-inactive.svg") center center no-repeat;
  }
  input[type="range"]::-moz-range-thumb {
    background: url("~@/assets/range-slider-thumb-inactive.svg") center center no-repeat;
  }
}
</style>
